'use client';

import { CheckCircle, Scale, XCircle } from 'lucide-react';

/**
 * ComparisonTable - Displays a competitive comparison for SEO and conversion
 * Used primarily for LiteBox vs inflatable trench shield positioning
 */
export default function ComparisonTable({ comparisonData, productName }) {
    if (!comparisonData?.advantages) {
        return null;
    }

    const { competitorType, advantages } = comparisonData;

    return (
        <section className="mb-8 sm:mb-12 px-2">
            <div className="bg-white shadow-md overflow-hidden border border-gray-100">
                <div className="bg-linear-to-r from-gray-900 to-gray-800 px-4 sm:px-6 py-4">
                    <h2 className="text-xl font-bold text-white flex items-center">
                        <Scale className="text-brand w-5 h-5 mr-2" />
                        {productName} vs {competitorType}
                    </h2>
                    <p className="text-gray-300 text-sm mt-1">
                        Why contractors are switching from{' '}
                        {competitorType.toLowerCase()} to {productName}
                    </p>
                </div>
                <div className="p-4 sm:p-6">
                    {/* Mobile: Card layout */}
                    <div className="block lg:hidden space-y-4">
                        {advantages.map((row, index) => (
                            <div
                                key={index}
                                className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                            >
                                <h3 className="font-bold text-gray-900 mb-3 text-center">
                                    {row.factor}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-start gap-2 bg-red-50 p-3 rounded-lg">
                                        <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="text-xs font-medium text-red-700 uppercase tracking-wide">
                                                {competitorType}
                                            </span>
                                            <p className="text-gray-700 text-sm mt-1">
                                                {row.competitor}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2 bg-green-50 p-3 rounded-lg">
                                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="text-xs font-medium text-green-700 uppercase tracking-wide">
                                                {productName}
                                            </span>
                                            <p className="text-gray-700 text-sm mt-1">
                                                {row.litebox}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: Table layout */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-gray-200">
                                    <th className="text-left py-3 px-4 font-bold text-gray-900">
                                        Factor
                                    </th>
                                    <th className="text-left py-3 px-4 font-bold text-red-700 bg-red-50">
                                        <div className="flex items-center gap-2">
                                            <XCircle className="w-4 h-4" />
                                            {competitorType}
                                        </div>
                                    </th>
                                    <th className="text-left py-3 px-4 font-bold text-green-700 bg-green-50">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4" />
                                            {productName}
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {advantages.map((row, index) => (
                                    <tr
                                        key={index}
                                        className={`border-b border-gray-100 ${
                                            index % 2 === 0
                                                ? 'bg-gray-50/50'
                                                : ''
                                        }`}
                                    >
                                        <td className="py-4 px-4 font-semibold text-gray-800">
                                            {row.factor}
                                        </td>
                                        <td className="py-4 px-4 text-gray-600 bg-red-50/30">
                                            {row.competitor}
                                        </td>
                                        <td className="py-4 px-4 text-gray-700 font-medium bg-green-50/30">
                                            {row.litebox}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* SEO-friendly summary paragraph */}
                    <div className="mt-6 p-4 bg-brand/5 rounded-lg border border-brand/10">
                        <p className="text-gray-700 text-sm leading-relaxed">
                            <strong className="text-brand">
                                The bottom line:
                            </strong>{' '}
                            While {competitorType.toLowerCase()} may seem
                            convenient, the {productName} delivers superior
                            reliability without the ongoing costs and risks. No
                            air compressors to haul, no bags to replace, no
                            pressure to monitor — just solid aluminium
                            protection that works every time.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
